import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import DataTable from 'datatables.net-bs5';
import 'datatables.net-bs5/css/dataTables.bootstrap5.min.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { icon } from '@fortawesome/fontawesome-svg-core';
import { faHeartPulse, faMapLocationDot, faTableColumns, faCircleCheck, faCircleExclamation, faCircleXmark, faTable } from '@fortawesome/free-solid-svg-icons';
import MultiLineChart from '../graphs/MultiLineChart';
import { apiFetch } from '../../api/apiClient';
import { usePortfolioDateFormatter } from '../../utils/usePortfolioDateFormatter';
import StatusBar from '../graphs/StatusBar';
import { formatTemperature } from '../../util/temperature';
import { useTemperatureUnit } from '../../util/useTemperatureUnit';

const PAGE_LENGTH_OPTIONS = [20, 50, 100, -1];
const PAGE_LENGTH_LABELS = [20, 50, 100, 'All'];

const STATUS_SORT = { green: 0, yellow: 1, red: 2 };

const comparePowerblockIds = (a, b) => {
  const parse = (id) => {
    const match = String(id).match(/^([A-Za-z]*)(\d*)$/);
    return { prefix: match?.[1] ?? String(id), number: match?.[2] ? Number(match[2]) : 0 };
  };
  const pa = parse(a);
  const pb = parse(b);
  if (pa.prefix !== pb.prefix) {
    return pa.prefix.localeCompare(pb.prefix);
  }
  return pa.number - pb.number;
};

function toSeriesPoints(rows, valueKey) {
  return rows.map((p) => ({ date: p.date, value: p[valueKey] }));
}

function destroyDataTable(dataTableRef, restore = true) {
  if (!dataTableRef.current) {
    return;
  }

  try {
    dataTableRef.current.destroy(restore);
  } catch (error) {
    console.warn('Failed to destroy DataTable instance:', error);
  } finally {
    dataTableRef.current = null;
  }
}

function resetTableHost(tableHostRef, dataTableRef) {
  destroyDataTable(dataTableRef);

  if (tableHostRef.current) {
    tableHostRef.current.innerHTML = '';
  }
}

function formatTableInfo(_settings, start, end, _max, total) {
  if (total === 0) {
    return 'Showing 0 to 0 out of 0 entries';
  }

  return `Showing ${start} to ${end} out of ${total} entries`;
}

function statusColumn() {
  return {
    title: 'Status',
    render: (cellData, type) => {
      if (type === 'sort' || type === 'type') {
        return STATUS_SORT[cellData] ?? 3;
      }

      return '';
    },
    createdCell: (cell, cellData) => {
      const status = ['green', 'yellow', 'red'].includes(cellData) ? cellData : null;
      if (!status) {
        cell.textContent = '—';
        cell.title = 'No performance reading in the current lookback';
        return;
      }
      cell.title = 'PV DC MW per panel versus the plant median';
      const faIcon =
        status === 'green'
          ? faCircleCheck
          : status === 'yellow'
          ? faCircleExclamation
          : faCircleXmark;
      const rendered = icon(faIcon);
      cell.innerHTML = rendered.html[0];
      const svg = cell.querySelector('svg');
      if (svg) {
        svg.classList.add(status);
      }
    },
  };
}

function mwColumn(title) {
  return {
    title,
    render: (cellData, type) => {
      if (type === 'sort' || type === 'type') {
        return cellData ?? 0;
      }
      if (cellData == null || cellData === '') {
        return '--';
      }
      return `${cellData} MW`;
    },
  };
}

function mwhColumn(title) {
  return {
    title,
    render: (cellData, type) => {
      if (type === 'sort' || type === 'type') {
        return cellData ?? 0;
      }
      if (cellData == null || cellData === '') {
        return '--';
      }
      return `${cellData} MWh`;
    },
  };
}

function tempColumn(title, temperatureUnit) {
  return {
    title,
    render: (cellData, type) => {
      if (type === 'sort' || type === 'type') {
        return cellData ?? 0;
      }

      return cellData ? formatTemperature(cellData, temperatureUnit, { fallback: '--' }) : '--';
    },
  };
}

function textColumn(title) {
  return {
    title,
    render: (cellData, type) => {
      if (type === 'sort' || type === 'type') {
        return cellData ?? '';
      }

      return cellData || '--';
    },
  };
}

function availabilityColumn() {
  return {
    title: 'Availability',
    render: (cellData, type) => {
      if (type === 'sort' || type === 'type') {
        return cellData ?? 0;
      }

      return '';
    },
    createdCell: (cell, cellData) => {
      const value = Number(cellData);
      if (Number.isNaN(value)) {
        cell.textContent = '--';
        return;
      }

      const barColor =
        value <= 25 ? 'bg-danger text-black' :
        value <= 74 ? 'bg-warning text-black' :
        'bg-success';
      cell.innerHTML = `<div class="progress justify-content-start" role="progressbar" aria-valuenow="${value}" aria-valuemin="0" aria-valuemax="100"><div class="progress-bar progress-bar-striped progress-bar-animated ${barColor}" style="width: ${value}%"><span>${value}%</span></div></div>`;
    },
  };
}

const PB_SOLAR_COLOR = '#D07F15';
const PB_BESS_COLOR = '#008900';

function fmtPbValue(value, suffix = '') {
  if (value == null || value === '') {
    return '—';
  }
  return suffix ? `${value}${suffix}` : String(value);
}

function seriesHasPoints(series) {
  return Array.isArray(series) && series.some((s) => Array.isArray(s?.data) && s.data.length > 0);
}

function isSolarProductionAvailable(data) {
  const tiles = Object.values(data ?? {});
  if (tiles.length === 0) {
    return false;
  }
  if (tiles.some((tile) => tile?.solar_production_available === false)) {
    return false;
  }
  if (tiles.some((tile) => tile?.solar_production_available === true)) {
    return true;
  }
  return tiles.some((tile) => Number(tile?.pv_dc_power) > 0.05);
}

const PB_GENERATION_SOLAR_CONFIG = {
  duration: '24hr',
  domain: [0, 5],
  tickValues: [5, 0],
  unit: 'MW',
  tooltipUnit: '',
  midlines: [{ value: 2.5, label: '2.5 MW' }],
  wrapperClassName: 'solar mb-3',
  height: 200,
  headerLeft: <strong>GENERATED</strong>,
  headerRight: <strong>SOLAR</strong>,
};

const PB_BESS_CONFIG = {
  duration: '24hr',
  domain: [-2, 2],
  tickValues: [2, 0, -2],
  unit: 'MW',
  tooltipUnit: '',
  midlines: [
    { value: 1, label: '1 MW' },
    { value: -1, label: '-1 MW' },
  ],
  wrapperClassName: 'storage mb-3',
  height: 200,
  headerLeft: <strong>CHARGED / DISCHARGED</strong>,
  headerRight: <strong>STORAGE</strong>,
};

function SectorPowerBlocks({ sectorId, portfolioName, onTooltip }) {
  const temperatureUnit = useTemperatureUnit();
  const dateFormatter = usePortfolioDateFormatter({ portfolioName });
  const [t1xfmrView, setT1xfmrView] = useState(true);
  const [data, setData] = useState(null);
  const [activePB, setActivePB] = useState(null);
  const [activePBData, setActivePBData] = useState(null);
  const [solarChartData, setSolarChartData] = useState([]);
  const [bessChartData, setBessChartData] = useState([]);
  const tableHostRef = useRef(null);
  const dataTableRef = useRef(null);

  useEffect(() => {
    if (data && Object.keys(data).includes(activePB)) {
      setActivePBData(data[activePB]);
    } else {
      setActivePBData(null);
    }
  }, [data, activePB]);
  
  useEffect(() => {
    if (sectorId == null || Number.isNaN(Number(sectorId))) {
      return;
    }
    let cancelled = false;

    (async () => {
      try {
        const raw = await apiFetch(`/api/metrics/portfolio/${portfolioName}/sector/powerblocks/${sectorId}`);
        if (!cancelled) {
          setData(raw);

          const keys = Object.keys(raw).sort(comparePowerblockIds);
          if (keys.length > 0) {
            setActivePB(keys[0]);
          }
        }
      } catch (apiError) {
        console.warn(
          `API fetch failed for /api/metrics/portfolio/${portfolioName}/sector/powerblocks/${sectorId}:`,
          apiError
        );
        if (!cancelled) {
          setData(null);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [sectorId, portfolioName]);

  useEffect(() => {
    if (!activePB) {
      return undefined;
    }
    let cancelled = false;

    setSolarChartData([]);
    setBessChartData([]);

    (async () => {
      try {
        const [solarRaw, bessRaw] = await Promise.allSettled([
          apiFetch(`/api/graph/portfolio/${portfolioName}/powerblock/${activePB}/generation-solar?duration=24h`),
          apiFetch(`/api/graph/portfolio/${portfolioName}/powerblock/${activePB}/bess-profile?duration=24h`),
        ]);

        if (!cancelled) {
          if (solarRaw.status === 'fulfilled' && Array.isArray(solarRaw.value?.points)) {
            setSolarChartData(
              solarRaw.value.points
                .map(p => ({
                  date:  new Date(p.timestamp),
                  right: p.solarPowerMw ?? NaN,
                }))
                .filter(p => p.date != null)
            );
          }

          if (bessRaw.status === 'fulfilled' && Array.isArray(bessRaw.value?.points)) {
            setBessChartData(
              bessRaw.value.points
                .map(p => ({
                  date:  new Date(p.timestamp),
                  left:  p.dischargedMwh  ?? NaN,
                  right: p.storagePowerMw ?? NaN,
                }))
                .filter(p => p.date != null)
            );
          }
        }
      } catch (apiError) {
        console.warn(`PB graph fetch failed for ${activePB}:`, apiError);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [activePB, portfolioName]);

  const solarSeries = useMemo(
    () => [
      {
        label: 'SOLAR (MW)',
        color: PB_SOLAR_COLOR,
        data: toSeriesPoints(solarChartData, 'right'),
      },
    ],
    [solarChartData],
  );

  const bessSeries = useMemo(
    () => [
      {
        label: 'CHARGED / DISCHARGED (MWh)',
        color: PB_BESS_COLOR,
        dash: '6,4',
        data: toSeriesPoints(bessChartData, 'left'),
      },
      {
        label: 'STORAGE (MW)',
        color: PB_BESS_COLOR,
        data: toSeriesPoints(bessChartData, 'right'),
      },
    ],
    [bessChartData],
  );

  const toggleT1xfmrView = () => {
    setT1xfmrView((prev) => !prev);
  };

  const tableRows = useMemo(() => {
    if (!data) return [];
    return Object.entries(data)
      .sort(([a], [b]) => comparePowerblockIds(a, b))
      .map(([label, pb]) => ({ label, pb }));
  }, [data]);
  const solarProductionAvailable = isSolarProductionAvailable(data);

  useEffect(() => {
    if (t1xfmrView) {
      resetTableHost(tableHostRef, dataTableRef);
      return undefined;
    }

    const host = tableHostRef.current;
    if (!host || tableRows.length === 0) {
      return undefined;
    }

    resetTableHost(tableHostRef, dataTableRef);

    const table = document.createElement('table');
    table.className = 'table table-striped w-100 sector-powerblocks-table';
    table.style.width = '100%';
    host.appendChild(table);

    const columns = [
      statusColumn(),
      textColumn('Device'),
      textColumn('Alarms'),
      tempColumn('Current Temp', temperatureUnit),
      tempColumn('Max Temp', temperatureUnit),
      tempColumn('Min Temp', temperatureUnit),
      availabilityColumn(),
      mwhColumn('Production Today'),
      mwColumn('Storage'),
      mwColumn('AC Power'),
      mwColumn('PV DC Power'),
      textColumn('% of Total'),
      textColumn('Current SOC'),
      textColumn('Historical SOC'),
      textColumn('RTE'),
    ];

    const tableData = tableRows.map(({ label, pb }) => [
      solarProductionAvailable ? pb.status : null,
      label || '--',
      pb.alarms,
      pb.temperature,
      pb.max_temp,
      pb.min_temp,
      pb.availability,
      pb.production,
      pb.storage,
      pb.ac_power,
      pb.pv_dc_power,
      pb.percent_of_total,
      pb.current_soc,
      pb.historical_soc,
      pb.rte,
    ]);

    dataTableRef.current = new DataTable(table, {
      data: tableData,
      columns,
      pageLength: 20,
      lengthMenu: [PAGE_LENGTH_OPTIONS, PAGE_LENGTH_LABELS],
      searching: true,
      paging: true,
      ordering: true,
      autoWidth: false,
      initComplete: () => {
        host.querySelectorAll('table.sector-powerblocks-table').forEach((el) => {
          el.style.width = '100%';
        });
        host.querySelectorAll('thead th').forEach((th) => {
          th.style.removeProperty('width');
          th.style.removeProperty('min-width');
          th.style.removeProperty('max-width');
        });
        dataTableRef.current?.columns.adjust().draw(false);
      },
      layout: {
        topStart: 'pageLength',
        topEnd: {
          search: {
            placeholder: 'Search table...',
          },
        },
        bottomStart: {
          info: {
            callback: formatTableInfo,
          },
        },
        bottomEnd: {
          paging: {
            numbers: 5,
          },
        },
      },
    });

    return () => {
      resetTableHost(tableHostRef, dataTableRef);
    };
  }, [tableRows, t1xfmrView, solarProductionAvailable, temperatureUnit]);

  useEffect(() => () => {
    resetTableHost(tableHostRef, dataTableRef);
  }, []);

  const powerblockIds = data ? Object.keys(data).sort(comparePowerblockIds) : [];
  const hasPowerblocks = powerblockIds.length > 0;
  const hasSolarChart = seriesHasPoints(solarSeries);
  const hasBessChart = seriesHasPoints(bessSeries);
  const statusClass = ['green', 'yellow', 'red'].includes(activePBData?.status)
    ? activePBData.status
    : null;

  return (
    <div id="plantpowerblocks" className="panel p-3 mb-5">
      <div className="header d-flex justify-content-between pb-3">
        <div className="left d-flex gap-3 align-items-center">
          <span><FontAwesomeIcon icon={faHeartPulse} /> POWERBLOCKS - XFMR {sectorId}</span>
          {!hasPowerblocks && data !== null && (
            <span className="badge text-bg-secondary" title="No reading in the current telemetry lookback">
              Empty — no recent reading
            </span>
          )}
        </div>
        <div className="right d-flex align-items-center gap-3">
          <div className="powerblock-status-legend" aria-label="Tile color key: PV DC MW per panel versus the plant median">
            <span className="label">PV DC MW per panel vs plant median</span>
            <span><i className="swatch green" aria-hidden="true" /> ≤ 1%</span>
            <span><i className="swatch yellow" aria-hidden="true" /> 1–3%</span>
            <span><i className="swatch red" aria-hidden="true" /> &gt; 3%</span>
          </div>
          <button className="d-none"><FontAwesomeIcon icon={faMapLocationDot} /></button>
          <button onClick={toggleT1xfmrView} aria-label={t1xfmrView ? 'Show powerblock table' : 'Show powerblock tiles'}><FontAwesomeIcon icon={t1xfmrView ? faTableColumns : faTable} /></button>
        </div>
      </div>
      <div className={`body gap-5 justify-content-between pt-3 align-items-start ${ t1xfmrView ? 'd-flex' : 'd-none' }`}>
        <div className="col-8">
          {!hasPowerblocks ? (
            <p className="mb-0 text-muted">No powerblock readings in the current lookback.</p>
          ) : (
            <>
              {solarProductionAvailable ? (
                <ul className="powerblocks mb-5">
                  {powerblockIds.map((id) => {
                    const tileStatus = ['green', 'yellow', 'red'].includes(data[id]?.status)
                      ? data[id].status
                      : '';
                    return (
                      <li key={id} className={`${tileStatus} ${activePB === id ? 'selected' : ''}`.trim()}>
                        <Link onClick={() => setActivePB(id)} className="w-100 h-100 d-flex align-items-center justify-content-center">
                          {id}
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <p className="powerblocks-unavailable mb-5">
                  Solar Production Status Unavailable : Insufficient Data
                </p>
              )}
              {hasSolarChart ? (
                <MultiLineChart
                  {...PB_GENERATION_SOLAR_CONFIG}
                  series={solarSeries}
                  onTooltip={onTooltip}
                  timeZone={dateFormatter.timeZone}
                />
              ) : (
                <p className="mb-3 text-muted small">No solar readings in the selected range.</p>
              )}
              {hasBessChart ? (
                <MultiLineChart
                  {...PB_BESS_CONFIG}
                  series={bessSeries}
                  onTooltip={onTooltip}
                  timeZone={dateFormatter.timeZone}
                />
              ) : (
                <p className="mb-3 text-muted small">No storage readings in the selected range.</p>
              )}
            </>
          )}
        </div>
        <div className="col">
          {activePBData !== null &&
          <div className="details">
            <div className="d-flex flex-row align-items-center gap-2 justify-content-between">
              <strong>POWERBLOCK</strong> {activePB}
            </div>
            <div className="d-flex flex-row align-items-center gap-2 justify-content-between">
              <strong title="PV DC MW per panel versus the plant median">STATUS</strong>
              {solarProductionAvailable && statusClass ? (
                <FontAwesomeIcon
                  className={statusClass}
                  icon={
                    statusClass === 'green'
                      ? faCircleCheck
                      : statusClass === 'yellow'
                      ? faCircleExclamation
                      : faCircleXmark
                  }
                />
              ) : (
                <span title={solarProductionAvailable ? 'No performance reading in the current lookback' : 'Solar production status unavailable'}>—</span>
              )}
            </div>
            <div className="d-flex flex-row align-items-center gap-2 justify-content-between">
              <strong>UP TIME</strong> {fmtPbValue(activePBData.up_time)}
            </div>
            <div className="d-flex flex-row align-items-center gap-2 justify-content-between">
              <strong>% of TOTAL</strong>
              {activePBData.percent_of_total != null
                ? <StatusBar value={Number(activePBData.percent_of_total)} type="status" />
                : '—'}
            </div>
            <div className="d-flex flex-row align-items-center gap-2 justify-content-between">
              <strong>CURRENT SOC</strong>
              {activePBData.current_soc != null
                ? <StatusBar value={Number(activePBData.current_soc)} type="status" />
                : '—'}
            </div>
            <div className="d-flex flex-row align-items-center gap-2 justify-content-between">
              <strong>PRODUCTION TODAY</strong> {fmtPbValue(activePBData.production, ' MWh')}
            </div>
            <div className="d-flex flex-row align-items-center gap-2 justify-content-between">
              <strong>CHARGED ENERGY</strong> {fmtPbValue(activePBData.charged_energy, ' MWh')}
            </div>
            <div className="d-flex flex-row align-items-center gap-2 justify-content-between">
              <strong>DISCHARGED ENERGY</strong> {fmtPbValue(activePBData.discharged_energy, ' MWh')}
            </div>
            <div className="d-flex flex-row align-items-center gap-2 justify-content-between">
              <strong>AC POWER</strong> {fmtPbValue(activePBData.ac_power, ' MW')}
            </div>
            <div className="d-flex flex-row align-items-center gap-2 justify-content-between">
              <strong>PV DC POWER</strong> {fmtPbValue(activePBData.pv_dc_power, ' MW')}
            </div>
          </div>}
        </div>
      </div>
      <div className={`body pt-3 ${ t1xfmrView ? 'd-none' : 'd-block' }`}>
        {tableRows.length === 0 && (
          <p className="mb-2 text-muted">No powerblock data available</p>
        )}
        <div
          ref={tableHostRef}
          className="sector-powerblocks-table-host"
        />
      </div>
    </div>
  );
}

export default SectorPowerBlocks;
